import { useCallback, useEffect, useMemo, useState } from 'react';
import { MealListInterface, NewMealInterface } from '../../../../../../shared/type/meal.type';
import { useFormatDate } from './useFormatDate';
import { useParams } from 'react-router-dom';
import { AxiosResponse, HttpStatusCode } from 'axios';
import {
  MealInterface,
  MealWithCategoryInterface
} from '../../../../../../shared/type/mealsMenu.type';
import { AllergenInterface } from '../../../../../../shared/type/allergen.type';
import { SimpleCourseInterface } from '../../../../../../shared/type/course.type';
import {
  MealToDayDayInterface,
  MealToDayInterface
} from '../../../../../../shared/type/mealToDay.type';
import { useApi } from '../../../../../../context/ApiProvider';
import { useAlertContext } from '../../../../../../context/AlertProvider';
import { groupMealsByCategory } from './util';

const DEFAULT_MEAL = {
  courseId: null,
  name: '',
  description: '',
  allergenIds: [],
  isNewMeal: true
};

export const DEFAULT_MEAL_FROM_BASE = {
  id: 0,
  name: '',
  description: '',
  allergens: [],
  categoryIds: [] as number[],
  isNewMeal: false
};

export const useAddEditMealPlan = () => {
  const { id: tenantId } = useParams();
  const { setShow, setAlertProperties } = useAlertContext();
  const {
    apiTenantCourseController,
    apiTenantAllergenController,
    apiTenantMealController,
    apiTenantMealToDayController,
    apiTenantMealMealController
  } = useApi();
  const { currentDate, setCurrentDate, formattedDate } = useFormatDate();

  const [mealCategories, setMealCategories] = useState<SimpleCourseInterface[]>([]);
  const mealCategoriesToSelect = mealCategories.map((category) => ({
    label: category.name,
    value: category.id
  }));

  const [mealMenuList, setMealMenuList] = useState<MealListInterface[]>([]);
  const menusToSelect = mealMenuList.map((list) => ({
    label: list.name,
    value: list.id
  }));

  const [selectedMenuList, setSelectedMenuList] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number[]>([]);

  const [allergens, setAllergens] = useState<AllergenInterface[]>([]);
  const allergensToSelect = allergens.map((unit) => ({
    label: `${unit.name} (${unit.formalNumber})`,
    value: unit.id
  }));

  const [mealPlans, setMealPlans] = useState<MealToDayDayInterface[]>([]);
  const [mealsForDay, setMealsForDay] = useState<NewMealInterface[]>([]);

  const [newMealFormData, setNewMealFormData] = useState<NewMealInterface>({
    ...DEFAULT_MEAL,
    tenantId: Number(tenantId),
    when: formattedDate
  });
  const [selectedCategoryForBaseMeal, setSelectedCategoryForBaseMeal] = useState<number[]>([]);
  const [mealFromBaseFormData, setMealFromBaseFormData] = useState<
    {
      id: number;
      name: string;
      description: string;
      allergens: AllergenInterface[];
      categoryIds: number[];
    }[]
  >([]);
  console.log(mealFromBaseFormData);
  const [availableMeals, setAvailableMeals] = useState<MealInterface[]>([]);

  const filtredAvailableMeals = useMemo(() => {
    if (selectedMenuList.length === 0) {
      return availableMeals.filter((meal) => meal.mealMenuId === Number(tenantId));
    }
    return availableMeals.filter((meal) => selectedMenuList.includes(meal.mealMenuId));
  }, [availableMeals, selectedMenuList]);

  const availableMealsToSelect = filtredAvailableMeals.map((meal) => ({
    label: meal.name,
    value: meal.id
  }));

  const fetchMealPlans = async () => {
    try {
      const response: AxiosResponse<MealToDayInterface> = await apiTenantMealToDayController(
        ''
      ).post('', {
        tenantId: Number(tenantId),
        year: currentDate.year,
        month: currentDate.month
      });

      if (response.status !== HttpStatusCode.Ok) return;

      const mealsPlanForSelectedDay = response.data.days.find(
        (meal) => meal.when === formattedDate
      );

      const mealsForDayFromPlan =
        mealsPlanForSelectedDay?.mealsToCourse?.map((meal) => ({
          tenantId: Number(tenantId),
          when: formattedDate,
          courseId: meal.courseId,
          id: meal.mealDto?.id || undefined,
          name: meal.mealDto?.name || '',
          description: meal.mealDto?.description || '',
          allergenIds: meal.mealDto?.allergens.map((allergen) => allergen.id) || [],
          isNewMeal: false,
          mealToDayId: meal.mealToDayId
        })) || [];

      setMealsForDay(mealsForDayFromPlan);
      setMealPlans(response.data.days);
    } catch (error: any) {
      console.error(error);
    }
  };

  const deleteAssignement = async (mealToDayId: number | string) => {
    await apiTenantMealToDayController('delete-assignment').post('', { id: mealToDayId });
  };

  const fetchMealCategories = useCallback(async () => {
    try {
      const response: AxiosResponse<SimpleCourseInterface[]> = await apiTenantCourseController(
        'simple-list'
      ).get(`/${tenantId}`);
      setMealCategories(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [tenantId]);

  const fetchMealMenuList = useCallback(async () => {
    try {
      const response: AxiosResponse<MealListInterface[]> = await apiTenantMealMealController(
        'list-only-for-tenant'
      ).get('', {
        params: { tenantId: Number(tenantId) }
      });
      setMealMenuList(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [tenantId]);

  const fetchAllergens = useCallback(async () => {
    try {
      const response: AxiosResponse<AllergenInterface[]> = await apiTenantAllergenController(
        ''
      ).get(`/${tenantId}`);
      setAllergens(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [tenantId]);

  const fetchMeals = useCallback(async () => {
    try {
      const response: AxiosResponse<MealInterface[]> = await apiTenantMealController(
        'by-tenant'
      ).get(`/${tenantId}`);
      setAvailableMeals(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchAllergens();
    fetchMealCategories();
    fetchMeals();
    fetchMealMenuList();
  }, [tenantId]);

  useEffect(() => {
    fetchMeals();
  }, [tenantId, selectedMenuList]);

  useEffect(() => {
    fetchMealPlans();
  }, [tenantId, currentDate.month]);

  useEffect(() => {
    const mealsPlanForSelectedDay = mealPlans.find((meal) => meal.when === formattedDate);
    const mealsForDayFromPlan =
      mealsPlanForSelectedDay?.mealsToCourse?.map((meal) => ({
        tenantId: Number(tenantId),
        when: formattedDate,
        courseId: meal.courseId,
        id: meal.mealDto?.id || undefined,
        name: meal.mealDto?.name || '',
        description: meal.mealDto?.description || '',
        allergenIds: meal.mealDto?.allergens.map((allergen) => allergen.id) || [],
        isNewMeal: false,
        mealToDayId: meal.mealToDayId
      })) || [];

    setMealsForDay(mealsForDayFromPlan);
  }, [formattedDate]);

  const addNewMealToMealList = () => {
    const mealsToUpdate = [...mealsForDay, newMealFormData];
    handleCreateNewMeals(mealsToUpdate);
  };

  const handleDeleteMeal = (mealId: number | string) => {
    setMealsForDay((prev) => prev.filter((meal) => meal.mealToDayId !== mealId));
    deleteAssignement(mealId);
  };

  const handleCreateNewMeals = async (mealsToSubmit: NewMealInterface[]) => {
    const newMealsToAdd = mealsToSubmit.filter((meal) => meal.isNewMeal);
    if (newMealsToAdd.length === 0) return;

    try {
      for (const meal of newMealsToAdd) {
        const response = await apiTenantMealToDayController('create-assignment-with-meal').post(
          '',
          {
            tenantId,
            when: formattedDate,
            courseId: meal.courseId,
            name: meal.name,
            description: meal.description,
            allergenIds: meal.allergenIds
          }
        );

        if (response.status === HttpStatusCode.Ok) {
          setAlertProperties({
            timeout: 9000,
            title: 'Sukces',
            description: 'Poprawnie dodano danie',
            status: 'success'
          });
          setShow(true);
        }
      }
      setAlertProperties({
        timeout: 9000,
        title: 'Sukces',
        description: 'Pomyślnie dodano nowe posiłki',
        status: 'success'
      });
      setShow(true);
    } catch (error) {
      console.error('Error adding new meals:', error);
      setAlertProperties({
        timeout: 9000,
        title: 'Błąd',
        description: 'Nie udało się dodać nowych posiłków',
        status: 'error'
      });
      setShow(true);
    } finally {
      fetchMealPlans();
      setNewMealFormData({ ...DEFAULT_MEAL, tenantId: Number(tenantId), when: formattedDate });
    }
  };

  const handleAssignMealsFromBase = async () => {
    if (mealFromBaseFormData.length === 0) return;

    try {
      // 1. Rozwijamy mealFromBaseFormData do NewMealInterface[]
      const newMeals: NewMealInterface[] = mealFromBaseFormData.flatMap((meal) =>
        meal.categoryIds.map((categoryId) => ({
          tenantId: Number(tenantId),
          when: formattedDate,
          courseId: categoryId,
          id: meal.id,
          name: meal.name,
          description: meal.description,
          allergenIds: meal.allergens.map((a) => a.id),
          isNewMeal: false
        }))
      );

      // 2. Łączymy z istniejącymi daniami z dnia
      const allMeals = [...mealsForDay, ...newMeals];

      // 3. Grupujemy dania per kategoria => [{courseId, mealIds[]}]
      const payloads = groupMealsByCategory(allMeals, Number(tenantId), formattedDate);

      // 4. Aktualizacja podglądu lokalnego
      setMealsForDay(allMeals);

      // 5. Wysyłka do API
      for (const payload of payloads) {
        const isMulti = 'mealIds' in payload;
        const endpoint = isMulti ? 'create-multi-assignment' : 'create-assignment';

        await apiTenantMealToDayController(endpoint).post('', payload);
      }

      setAlertProperties({
        timeout: 9000,
        title: 'Sukces',
        description: 'Dania z bazy zostały poprawnie przypisane',
        status: 'success'
      });
      setShow(true);
    } catch (error) {
      console.error('Błąd przypisywania dań z bazy:', error);
      setAlertProperties({
        timeout: 9000,
        title: 'Błąd',
        description: 'Nie udało się przypisać dań z bazy',
        status: 'error'
      });
      setShow(true);
    } finally {
      fetchMealPlans();
      setMealFromBaseFormData([]);
    }
  };

  const getCategoryName = (categoryId: number | undefined) => {
    const category = mealCategories.find((cat) => cat.id === categoryId);
    return category ? category.name : 'Unknown Category';
  };

  const uniqueCategories = Array.from(new Set(mealsForDay.map((meal) => meal.courseId)));

  return {
    newMealFormData,
    setNewMealFormData,
    currentDate,
    setCurrentDate,
    formattedDate,
    availableMeals,
    availableMealsToSelect,
    getCategoryName,
    mealCategoriesToSelect,
    allergens,
    allergensToSelect,
    mealFromBaseFormData,
    setMealFromBaseFormData,
    mealsForDay,
    uniqueCategories,
    addNewMealToMealList,
    handleAssignMealsFromBase,
    handleDeleteMeal,
    menusToSelect,
    selectedMenuList,
    setSelectedMenuList,
    selectedCategory,
    setSelectedCategory,
    selectedCategoryForBaseMeal,
    setSelectedCategoryForBaseMeal
  };
};
